/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import { Button, Col, Form, Row, Spinner, Toast, ToastContainer } from "react-bootstrap";
import packageJson from '../../../../../../package.json';

import { useAuth } from "../../../../../providers";
import { IFormSignupComponent } from "../../../business/core/index.js";
import { FormSignupControllerImpl, ProfileCodeValidateStrategy, SignupCodeValidateStrategy } from "../../../business/impl";

function useFormCodeValidation() {
    const [code, setCode] = useState<string>("");
    const [codeError, setCodeError] = useState(false);

    const validateCode = () => {
        if (!code || code.length !== 6) {
            setCodeError(true);
            return false;
        }
        setCodeError(false);
        return true;
    };

    return { code, setCode, codeError, validateCode };
}

async function resendSignupCode(
    formData: FormData,
    controller: FormSignupControllerImpl,
    translate: any,
    setToastingText: (text: string | null) => void
): Promise<void> {
    if (!formData) return;
    try {
        const response = await controller.signup(formData);
        if (response === "success") {
            setToastingText(translate('resendSuccess'));
        } else {
            setToastingText(translate(response));
        }
    } catch (error) {
        setToastingText(error.message);
    }
}

const emptySetStep = function (value: React.SetStateAction<number>): void {
    throw new Error("Function not implemented.");
}

export const FormCodeValidateComponent: React.FC<IFormSignupComponent> = ({
    setStep = emptySetStep,
    step = 2,
    formData = new FormData(),
    subtitle = 'signUpCodePhoneInfo',
    showVersion = false,
    feedback,
    onClose
}) => {
    const { setCallbackLogin, handleLogout } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const version = packageJson.version;
    const { t } = useTranslation('account');

    const [toastingText, setToastingText] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const { code, setCode, codeError, validateCode } = useFormCodeValidation();

    const strategyMap: { [key: number]: any } = {
        1: new ProfileCodeValidateStrategy(t),
        2: new SignupCodeValidateStrategy(navigate, t),
        3: new ProfileCodeValidateStrategy(t),
    };

    const leftButtonStrategyMap: { [key: number]: any } = {
        1: onClose,
        2: () => setStep(1),
        3: onClose,
    };

    const buttonTextMap: { [key: number]: string } = {
        1: t('validate_button'),
        2: t('signup_button'),
        3: t('validate_button'),
    };

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setIsLoading(true);

        if (!validateCode()) {
            setIsLoading(false);
            return;
        }

        if (!formData) {
            setIsLoading(false);
            return;
        }
        if (!formData.has("code")) {
            formData.append("code", code);
        } else {
            formData.set("code", code);
        }

        try {
            const strategy = strategyMap[step];
            const strategyResponse = await strategy.execute(formData);
            if (feedback) {
                setCode("");
                feedback(strategyResponse);
            }
        } catch (error: any) {
            if (error === "401") {
                setCallbackLogin(
                    {
                        goBack: true,
                        origin: location.pathname,
                    }
                );
                handleLogout();
                navigate('/auth');
            } else {
                setToastingText(t(error.message));
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleResend = async () => {
        setIsLoading(true);
        await resendSignupCode(formData, new FormSignupControllerImpl(), t, setToastingText);
        setIsLoading(false);
    };

    return (
        <>
            <p dangerouslySetInnerHTML={{ __html: t(subtitle) }} />
            <Form className="mt-4" onSubmit={handleSubmit}>
                <Form.Group className="mb-3" controlId="formCode">
                    <Form.Control
                        required
                        placeholder={t('placeholder_code')}
                        onChange={(e) => setCode(e.target.value)}
                        className="custom-input text-capitalize"
                        disabled={isLoading}
                        isInvalid={codeError}
                        autoFocus
                        autoComplete="off"
                        maxLength={6}
                        value={code}
                    />
                    <Form.Control.Feedback type="invalid">
                        {codeError ? t('feedback_code') : ""}
                    </Form.Control.Feedback>
                </Form.Group>
                <fieldset disabled={isLoading}>
                    <div className="row">

                        {step === 2 && (
                            <>
                                <div className="col-12 mb-3">
                                    <a href="#" onClick={handleResend} style={{ textDecoration: 'none', color: '#333333' }}>
                                        {t('resendCodeText')}
                                    </a>
                                </div>
                            </>
                        )}

                        <div className="col">
                            <Button
                                variant={step === 2 ? "primary" : "null"}
                                type="button"
                                className="px-4 button-flat bg-theme-secondary text-white"
                                onClick={leftButtonStrategyMap[step]}
                                disabled={isLoading}
                            >
                                {!isLoading ? t('back_button') : (
                                    <Spinner animation="border" size="sm" role="status">
                                        <span className="visually-hidden">...</span>
                                    </Spinner>
                                )}
                            </Button>
                        </div>
                        <div className="col d-flex flex-row-reverse">
                            <Button
                                variant="primary"
                                type="submit"
                                className="px-4 button-flat"
                                disabled={isLoading}
                            >
                                {isLoading ? (
                                    <Spinner animation="border" size="sm" role="status">
                                        <span className="visually-hidden">...</span>
                                    </Spinner>
                                ) : (
                                    buttonTextMap[step]
                                )}
                            </Button>
                        </div>
                    </div>
                    {showVersion &&
                        <Row>
                            <Col className="text-center mt-2">
                                <small style={{ color: '#999999' }}>v{version}</small>
                            </Col>
                        </Row>
                    }
                </fieldset>
            </Form>
            <ToastContainer position="top-end">
                <Toast
                    bg="danger"
                    onClose={() => setToastingText(null)}
                    show={toastingText !== null}
                    delay={5000}
                    autohide
                >
                    <Toast.Body className="text-white">{toastingText}</Toast.Body>
                </Toast>
            </ToastContainer>
        </>
    );
};
